import { Injectable, effect, inject, signal, untracked } from '@angular/core';

import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { get, gt, isEmpty, isEqual, isString } from 'lodash';
import { Observable, lastValueFrom, map, of, startWith } from 'rxjs';
import { InputValueTypes } from './iq-controls-action.models';
import { IqControlsStatusService } from './iq-controls-status.service';


export const ControlRuleType = 'dashboardControls';
export enum DashboardControlsConditionsTypes {
    Value = 'value',
}
type IsValues = 'contains' | 'equals' | 'greaterThan';

export interface ValueCondition {
    type: String;
    key: DashboardControlsConditionsTypes.Value;
    is: IsValues;
    value: {
        dashboardControlId: string;
        name: string;
        value: InputValueTypes;
    };
}


@Injectable({
    providedIn: 'root',
})
export class IqControlsRulesService {
    private _status = inject(IqControlsStatusService);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _registered = signal(false);

    constructor() {
        effect(() => {
            const registered = this._registered();

            if (!registered) return;

            untracked(() => {
                this._bus.dispatch({
                    name: 'type:rules:evaluator:register',
                    payload: {
                        evaluator: ({
                            dashboardControlId,
                            name,
                            is,
                            value,
                        }: {
                            dashboardControlId: string;
                            name: string;
                            is: IsValues;
                            value: InputValueTypes;
                        }): Promise<boolean> => {
                            return this.dashboardControlsControlValue(
                                dashboardControlId,
                                name,
                                is,
                                value,
                            );
                        },
                        type: 'dashboard-controls:control:value',
                    },
                });
            });
        });
    }

    register() {
        if (this._registered()) return;

        this._registered.set(true);
    }

    dashboardControlsControlValue(
        dashboardControlId: string,
        name: string,
        is: IsValues,
        value: InputValueTypes,
    ): Promise<boolean> {
        const formGroup = this._status.getFg(dashboardControlId);

        const eval$: Observable<boolean> = isEmpty(formGroup)
            ? of(false)
            : formGroup.valueChanges.pipe(
                  startWith(formGroup.value),
                  map((fgValue) => {
                      return this._compareData(
                          is,
                          get(fgValue, name) ?? '',
                          value,
                      );
                  }),
              );

        return lastValueFrom(eval$);
    }

    private _compareData(
        is: IsValues,
        value: InputValueTypes,
        data: InputValueTypes,
    ): boolean {
        return (
            {
                contains: (cache: InputValueTypes, data: InputValueTypes) => {
                    return isString(cache) && isString(data)
                        ? cache.includes(data)
                        : false;
                },
                equals: (cache: InputValueTypes, data: InputValueTypes) => {
                    return isEqual(cache, data);
                },
                greaterThan: (
                    cache: InputValueTypes,
                    data: InputValueTypes,
                ) => {
                    return gt(cache, data);
                },
            }?.[is]?.(value, data) ?? true
        );
    }
}
